import {useCallback, useEffect, useRef, type RefObject} from 'react';

// First match in document order wins — a leading search input, else the first control.
const FOCUSABLE_SELECTOR =
  'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [role="menuitem"]:not([tabindex^="-"]), button:not([disabled]):not([tabindex^="-"]), a[href], [tabindex]:not([tabindex^="-"])';

/** Non-trapping: focuses the first focusable in `ref` when `enabled`; returns `restoreFocus`. */
export default function useAutoFocusReturn<T extends HTMLElement = HTMLElement>(
  ref: RefObject<T | null>,
  enabled: boolean,
  preventScroll = false
): () => void {
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);
  const restoreFocus = useCallback(
    () => previouslyFocusedRef.current?.focus({preventScroll}),
    [preventScroll]
  );
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler -- imperative focus is a real side effect */
  useEffect(() => {
    if (!enabled) return;
    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus({preventScroll});
  }, [ref, enabled, preventScroll]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler */
  return restoreFocus;
}
